import { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowUp, ArrowDown, Check, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useTicketCategories, useCreateTicketCategory, useUpdateTicketCategory, useReorderTicketCategories } from '../../hooks/queries/useTicketCategories';
import { cn } from '../../lib/cn';
import type { TicketCategory } from '../../services/collaborators/ticketCategoryService';

interface EditingState {
  id: string;
  field: 'label' | 'description';
  value: string;
}

export function CategoriesTab() {
  const { data: categories = [], isLoading, error } = useTicketCategories(true);
  const { mutateAsync: updateCategory } = useUpdateTicketCategory();
  const { mutateAsync: reorder, isPending: isReordering } = useReorderTicketCategories();
  const createCategory = useCreateTicketCategory();

  // Formulaire « Nouvelle catégorie »
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPole, setNewPole] = useState('');
  const poles = useMemo(
    () => [...new Set(categories.map((c) => c.pole).filter(Boolean) as string[])].sort(),
    [categories],
  );

  async function submitCreate() {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Le libellé est requis');
      return;
    }
    try {
      await createCategory.mutateAsync({
        label,
        description: newDescription.trim() || null,
        pole: newPole || null,
      });
      toast.success(`Catégorie « ${label} » créée`);
      setCreating(false);
      setNewLabel('');
      setNewDescription('');
      setNewPole('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Création impossible : ${message}`);
    }
  }

  // Local ordered list for reordering
  const [orderedCategories, setOrderedCategories] = useState<TicketCategory[]>([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setOrderedCategories(categories);
    setHasOrderChanged(false);
  }, [categories]);

  useEffect(() => {
    if (editing) {
      editInputRef.current?.focus();
    }
  }, [editing]);

  function moveUp(index: number) {
    if (index === 0) return;
    const next = [...orderedCategories];
    const temp = next[index - 1];
    next[index - 1] = next[index]!;
    next[index] = temp!;
    setOrderedCategories(next);
    setHasOrderChanged(true);
  }

  function moveDown(index: number) {
    if (index === orderedCategories.length - 1) return;
    const next = [...orderedCategories];
    const temp = next[index + 1];
    next[index + 1] = next[index]!;
    next[index] = temp!;
    setOrderedCategories(next);
    setHasOrderChanged(true);
  }

  async function saveOrder() {
    try {
      await reorder(orderedCategories.map((c) => c.id));
      toast.success('Ordre enregistré');
      setHasOrderChanged(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur lors de l'enregistrement : ${message}`);
    }
  }

  function startEditing(cat: TicketCategory, field: 'label' | 'description') {
    setEditing({ id: cat.id, field, value: cat[field] ?? '' });
  }

  async function commitEdit() {
    if (!editing) return;
    const cat = orderedCategories.find((c) => c.id === editing.id);
    if (!cat) return;

    const trimmed = editing.value.trim();
    if (editing.field === 'label' && !trimmed) {
      toast.error('Le libellé ne peut pas être vide');
      return;
    }

    try {
      await updateCategory({
        id: editing.id,
        [editing.field]: trimmed || null,
      });
      toast.success('Mise à jour enregistrée');
      setEditing(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur : ${message}`);
    }
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function toggleActive(cat: TicketCategory) {
    try {
      await updateCategory({ id: cat.id, is_active: !cat.is_active });
      toast.success(cat.is_active ? 'Catégorie désactivée' : 'Catégorie activée');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur : ${message}`);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2
            className="text-xl font-bold text-cime-primary"
          >
            Catégories de tickets
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Les catégories permettent à l'IA de router les demandes vers le bon collaborateur,
            et servent de compétences aux avocats. Utilisez les flèches pour réorganiser,
            cliquez sur un libellé pour l'éditer.
          </p>
        </div>
        {!creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4" aria-hidden />
            Nouvelle catégorie
          </Button>
        )}
      </div>

      {/* Formulaire de création */}
      {creating && (
        <div className="bg-cime-surface rounded-cime border border-neutral-200 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1" htmlFor="new-cat-label">
                Libellé *
              </label>
              <input
                id="new-cat-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitCreate(); }}
                placeholder="Ex. : Droit de la propriété intellectuelle"
                className="w-full h-9 px-3 text-sm rounded-md border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:border-cime-primary"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1" htmlFor="new-cat-pole">
                Pôle
              </label>
              <select
                id="new-cat-pole"
                value={newPole}
                onChange={(e) => setNewPole(e.target.value)}
                className="w-full h-9 px-2 text-sm rounded-md border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:border-cime-primary"
              >
                <option value="">Aucun</option>
                {poles.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1" htmlFor="new-cat-desc">
              Description
            </label>
            <input
              id="new-cat-desc"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void submitCreate(); }}
              placeholder="Aide l'IA à classer les demandes dans cette catégorie…"
              className="w-full h-9 px-3 text-sm rounded-md border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:border-cime-primary"
            />
          </div>
          <p className="text-xs text-neutral-400">
            Avec un pôle, les demandes de cette catégorie apparaissent dans l'onglet Prospects ;
            sans pôle, dans l'onglet Autre. La catégorie devient aussitôt sélectionnable comme
            compétence sur les fiches collaborateurs.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreating(false)} disabled={createCategory.isPending}>
              Annuler
            </Button>
            <Button variant="primary" onClick={() => void submitCreate()} isLoading={createCategory.isPending}>
              Créer la catégorie
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-600 text-sm">
            Erreur lors du chargement. Veuillez réessayer.
          </div>
        ) : (
          <ul role="list" className="divide-y divide-neutral-100">
            {orderedCategories.map((cat, index) => {
              const isEditingLabel = editing?.id === cat.id && editing.field === 'label';
              const isEditingDesc = editing?.id === cat.id && editing.field === 'description';

              return (
                <li
                  key={cat.id}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 transition-colors',
                    !cat.is_active && 'opacity-50',
                  )}
                >
                  {/* Order arrows */}
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      aria-label={`Monter ${cat.label}`}
                      className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowUp className="w-3.5 h-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(index)}
                      disabled={index === orderedCategories.length - 1}
                      aria-label={`Descendre ${cat.label}`}
                      className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowDown className="w-3.5 h-3.5" aria-hidden />
                    </button>
                  </div>

                  {/* Slug pill */}
                  <div className="w-52 shrink-0">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono bg-neutral-100 text-neutral-500">
                      {cat.slug}
                    </span>
                  </div>

                  {/* Label & description */}
                  <div className="flex-1 min-w-0">
                    {/* Label */}
                    {isEditingLabel ? (
                      <div className="flex items-center gap-2">
                        <input
                          ref={editInputRef}
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void commitEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="flex-1 h-7 px-2 text-sm rounded-md border border-cime-primary bg-white text-neutral-900 focus:outline-none"
                          aria-label="Éditer le libellé"
                        />
                        <button
                          type="button"
                          onClick={() => void commitEdit()}
                          className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                          aria-label="Valider"
                        >
                          <Check className="w-3.5 h-3.5" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="p-1 text-neutral-400 hover:bg-neutral-100 rounded transition-colors"
                          aria-label="Annuler"
                        >
                          <X className="w-3.5 h-3.5" aria-hidden />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditing(cat, 'label')}
                        className="text-sm font-medium text-neutral-900 hover:text-cime-primary hover:underline transition-colors text-left"
                        title="Cliquer pour modifier"
                      >
                        {cat.label}
                      </button>
                    )}

                    {/* Description */}
                    {isEditingDesc ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          ref={editInputRef}
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void commitEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="flex-1 h-7 px-2 text-xs rounded-md border border-cime-primary bg-white text-neutral-600 focus:outline-none"
                          placeholder="Description optionnelle…"
                          aria-label="Éditer la description"
                        />
                        <button
                          type="button"
                          onClick={() => void commitEdit()}
                          className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                          aria-label="Valider"
                        >
                          <Check className="w-3.5 h-3.5" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="p-1 text-neutral-400 hover:bg-neutral-100 rounded transition-colors"
                          aria-label="Annuler"
                        >
                          <X className="w-3.5 h-3.5" aria-hidden />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditing(cat, 'description')}
                        className="block text-xs text-neutral-400 hover:text-neutral-600 hover:underline transition-colors text-left mt-0.5"
                        title="Cliquer pour modifier la description"
                      >
                        {cat.description ?? <span className="italic">Ajouter une description…</span>}
                      </button>
                    )}
                  </div>

                  {/* Active toggle */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cat.is_active}
                    onClick={() => void toggleActive(cat)}
                    title={cat.is_active ? 'Désactiver' : 'Activer'}
                    className={cn(
                      'relative shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary focus-visible:ring-offset-2',
                      cat.is_active ? 'bg-cime-primary' : 'bg-neutral-300',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform',
                        cat.is_active ? 'translate-x-4' : 'translate-x-1',
                      )}
                    />
                    <span className="sr-only">{cat.is_active ? 'Active' : 'Inactive'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Save order button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!hasOrderChanged || isReordering}
          isLoading={isReordering}
          onClick={() => void saveOrder()}
        >
          Enregistrer l'ordre
        </Button>
      </div>
    </div>
  );
}
